import { Link, NavLink } from "react-router-dom";
import { Moon, Sun, Menu, X, Sparkles } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const links = [
  { to: "/", label: "Home" },
  { to: "/shift-reader", label: "Shift Reader" },
  { to: "/library", label: "Library" },
  { to: "/community", label: "Community" },
  { to: "/stories", label: "Stories" },
  { to: "/journal", label: "Journal" },
  { to: "/founder", label: "Founder" },
  { to: "/membership", label: "Shift Room" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-home-link">
          <Sparkles className="h-4 w-4 text-accent" />
          <span className="font-mono text-[10px] sm:text-xs tracking-[0.28em] sm:tracking-[0.32em] uppercase">The Shift</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-xs font-mono uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={toggle}
            className="h-11 w-11 rounded-full flex items-center justify-center border border-border hover:border-accent/40 active:border-accent transition-colors"
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/access" className="text-xs font-mono tracking-widest hover:text-accent" data-testid="nav-access-link">
                {user.name}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-btn"
                className="text-xs font-mono tracking-widest"
              >
                Sign out
              </Button>
            </div>
          ) : (
            <Link to="/auth" className="hidden md:block" data-testid="nav-auth-link">
              <Button size="sm" className="btn-pill text-xs font-mono tracking-widest">
                Sign in
              </Button>
            </Link>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden h-11 w-11 flex items-center justify-center"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/40 bg-background/95 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3.5 text-sm font-mono uppercase tracking-[0.24em] border-b border-border/40 last:border-0 ${
                    isActive ? "text-accent" : "text-muted-foreground"
                  }`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link
                  to="/access"
                  onClick={() => setOpen(false)}
                  className="py-3.5 text-sm font-mono uppercase tracking-[0.24em] text-accent border-t border-border/40 mt-2"
                  data-testid="mobile-nav-access-link"
                >
                  My access ({user.name})
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                  }}
                  className="py-3.5 text-sm font-mono uppercase tracking-[0.24em] text-left text-muted-foreground"
                  data-testid="mobile-logout-btn"
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                onClick={() => setOpen(false)}
                className="py-3.5 text-sm font-mono uppercase tracking-[0.24em] text-accent border-t border-border/40 mt-2"
                data-testid="mobile-nav-auth-link"
              >
                Sign in / Create account
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
